<template>
  <div data-cy="upload-invoice-modal" :class="$style['upload-invoice-modal']">
    <div :class="$style.heading">
      <p>
        {{
          $t('account.pages.pageVendors.uploadInvoiceModal.invoiceDescription')
        }}
      </p>
      <p>
        {{
          $t(
            'account.pages.pageVendors.uploadInvoiceModal.submitMultipleBills',
            {
              type: fileType,
            },
          )
        }}
      </p>
    </div>

    <div :class="$style['requirements-checkbox-container']">
      <input
        type="checkbox"
        :checked="checkboxValue"
        :class="$style['requirements-checkbox']"
        @change="toggleCheckbox()"
      />
      <p>
        {{
          $t(
            'account.pages.pageVendors.uploadInvoiceModal.requirementsCheckbox',
            {
              type: fileType,
            },
          )
        }}
        <a :class="$style.link" :href="helpLink" target="_blank">
          {{
            $t(
              'account.pages.pageVendors.uploadInvoiceModal.billRequirements',
              {
                type: fileType,
              },
            )
          }}
        </a>
      </p>
    </div>

    <VeeForm
      ref="form"
      class="c-form"
      :class="$style.form"
      :initial-values="{
        fileUploaded: fileUploaded,
      }"
      @submit="onSubmit"
    >
      <div v-if="isInvoiceFlow || checkboxValue" class="c-form__row">
        <Field v-slot="{ errors }" v-bind="fileUploaded" name="fileUploaded">
          <BillBoxContainer :class="$style.uploadWrapper">
            <div>
              <p
                v-if="!fileUploaded"
                :class="$style['uploadWrapper__file-types']"
              >
                {{
                  $t(
                    'account.pages.pageVendors.uploadInvoiceModal.acceptedFileTypes',
                  )
                }}
              </p>
              <div>
                <UploadFileWidget
                  v-if="!fileUploaded"
                  ref="uploadFileWidget"
                  name="fileUploaded"
                  :empty-prompt="`\n ${$t(
                    'account.pages.pageVendors.uploadInvoiceModal.dragAndDrop',
                  )}`"
                  :upload-filters="uploadFilters"
                  :display-filters="displayFilters"
                  no-list
                  single-upload
                  theme="onboarding"
                  @complete="invoiceUploaded"
                  @uploadAttempt="uploadAttempt"
                />
                <div
                  v-if="currentUpload.id && fileUploaded"
                  :class="$style['uploaded-files']"
                >
                  <a
                    :href="currentUpload.downloadUrl"
                    :class="$style['uploaded-files__item']"
                    >{{ currentUpload.name }}</a
                  >
                  <CloseIcon
                    :class="$style.icon"
                    @click="removeDocs(currentUpload.id)"
                    @keydown="removeDocs(currentUpload.id)"
                  />
                </div>
              </div>
            </div>
          </BillBoxContainer>
          <p v-if="errors || uploadError" :class="$style.error" class="p-error">
            {{ errors[0] || uploadError }}
          </p>
        </Field>
      </div>
      <NavigationButtons
        :forward-label="$t('account.pages.pageVendors.nextStep')"
        :back-label="$t('account.pages.pageVendors.back')"
        :forward-disabled="!fileUploaded || !checkboxValue"
        forward-type="submit"
        @back-navigate="goBack"
      />
    </VeeForm>
  </div>
</template>

<script>
import { Form as VeeForm, Field } from 'vee-validate';
import { validateChildren } from '@/composables/validation';
import { mapGetters } from 'vuex';
import { ENTITY_TYPE, PAYMENTS_DOC_TYPE } from '@clearbanc/data-common-types';
import BillBoxContainer from '@/components/PayAnInvoiceModal/BillBoxContainer.vue';
import analytics, { SEGMENT_EVENTS } from '@/utils/analytics';
import CloseIcon from '@/assets/icons/close-icon.svg';
import NavigationButtons from '@/components/buttons/NavigationButtons.vue';

export default {
  components: {
    BillBoxContainer,
    VeeForm,
    Field,
    NavigationButtons,
    CloseIcon,
  },
  props: {
    fileUploaded: {
      type: Boolean,
      required: false,
    },
    requirementsChecked: {
      type: Boolean,
      required: true,
    },
  },
  setup() {
    const { hasError } = validateChildren();
    return {
      hasError,
    };
  },
  data() {
    return {
      checkboxValue: this.requirementsChecked,
      uploadError: null,
    };
  },
  computed: {
    ...mapGetters([
      'businessId',
      'currentUpload',
      'isInvoiceFlow',
      'isCashAdvanceFlow',
      'isDiligenceServiceReadDataEnabled',
      'isProcessDocumentOcrEnabled',
    ]),
    helpLink() {
      if (this.isInvoiceFlow) {
        return 'https://help.clear.co/s/article/How-to-submit-an-invoice';
      } else if (this.isCashAdvanceFlow) {
        return 'https://help.clear.co/s/article/How-to-apply-for-a-Cash-Advance';
      } else {
        return '';
      }
    },
    fileType() {
      if (this.isInvoiceFlow) {
        return PAYMENTS_DOC_TYPE.INVOICE;
      } else {
        return '';
      }
    },
    uploadFilters() {
      return {
        entity: ENTITY_TYPE.PAYMENTS,
        type: this.fileType,
        businessId: this.businessId,
      };
    },
    displayFilters() {
      return this.isDiligenceServiceReadDataEnabled
        ? {
            externalIdType: ENTITY_TYPE.BILL,
            documentType: this.fileType,
            businessId: this.businessId,
          }
        : {
            entity: ENTITY_TYPE.PAYMENTS,
            type: this.fileType,
            businessId: this.businessId,
          };
    },
  },
  methods: {
    toggleCheckbox() {
      this.checkboxValue = !this.checkboxValue;
      this.$emit('updateCheckbox', this.checkboxValue);
      analytics.track(SEGMENT_EVENTS.if_invoice_requirements_reviewed, {
        acknowledged: this.checkboxValue,
      });
    },
    async invoiceUploaded(uploadId) {
      const hasUploadId = !!uploadId;
      if (hasUploadId) {
        const uploadedFile = await this.getFileUpload(uploadId);
        if (uploadedFile) {
          this.$emit('updateFileUploaded', uploadId);
        } else {
          this.uploadError = this.$t('paymentsPage.processingRequestError');
          this.$refs.uploadFileWidget?.removeAllFiles();
        }
      }
    },
    async getFileUpload(uploadId) {
      const uploadedFile = await this.$store.dispatchApiAction(
        'GET_USER_UPLOAD_DETAILS',
        {
          id: uploadId,
          download: true,
          withProcessedData: this.isProcessDocumentOcrEnabled,
        },
      );
      if (uploadedFile) {
        this.$emit('getUploadedFile', uploadedFile);
      }
      return uploadedFile;
    },
    uploadAttempt() {
      analytics.track(SEGMENT_EVENTS.STARTED_BILL_UPLOAD);
    },
    async removeDocs(id) {
      this.$emit('removeFile');
      await this.$store.dispatchApiAction('DELETE_USER_UPLOAD', { id });
    },
    goBack() {
      analytics.track(SEGMENT_EVENTS.if_add_invoice_go_back_clicked);
      this.$emit('prev-tab');
    },
    async onSubmit(values) {
      this.$emit('updateFile');
      this.$emit('next-tab');
    },
  },
};
</script>

<style lang="less" module>
.upload-invoice-modal {
  max-width: 650px;
}

.heading {
  margin-bottom: 1rem;
  p {
    margin: 0;
  }
}

.form {
  text-align: left;
  padding: 0;
}

.info-box-content p:first-child {
  font-weight: 700 !important;
}

.requirements-checkbox {
  margin: 1rem;
  width: 1.3rem;
  height: 1.3rem;
  accent-color: #7f30ff;
}

.requirements-checkbox-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 1rem;
}

.uploadWrapper {
  margin: 0;
  width: 100%;
  padding: 2.5rem;
}

.uploadWrapper__file-types {
  margin-bottom: 0.5rem;
}

.uploaded-files {
  display: flex;
  align-items: center;
}

.uploaded-files__item {
  margin-right: 0.75rem;
}

.link {
  color: #145bce;
}

.error {
  margin: 0.5rem 0 0;
  font-weight: 400;
}

.uploadWrapper {
  margin: 0;
  width: 100%;
  padding: 2.5rem;
}

.uploadWrapper__file-types {
  margin-bottom: 0.5rem;
}

.uploaded-files {
  display: flex;
  align-items: center;
}

.uploaded-files__item {
  margin-right: 0.75rem;
}

.error {
  margin-top: 0.5rem;
  font-weight: 400;
}

.icon {
  height: 12px;
  width: 12px;
  cursor: pointer;
}
</style>
